import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DEFAULT_PUBLIC_DEALS_RETAILER,
  getPublicDealsPath,
  getPublicDealsRetailer,
  CLEARANCE_PATH,
  STORES_INDEX_PATH,
  type PublicDealsRetailer,
} from "../config/publicDeals";
import useSeo from "../hooks/useSeo";
import usePageBackground from "../hooks/usePageBackground";
import { useTheme } from "../context/ThemeContext";
import { CLEARANCE_SEO, HOME_SEO, homeFeedSeo } from "../seo/pages";
import NotFoundPage from "./NotFoundPage";
import { homeDealPath } from "./HomeDealPage";
import { useLayoutMode, type LayoutMode } from "../utils/layoutModePreference";
import { browsePublicProducts, getPublicAffiliateLink } from "../services/ProductsService";
import ShareDealSheet from "../components/ShareDealSheet";
import { getProductImageUrl } from "../utils/imageUtils";
import { getGuestDealDiscount, getGuestDealPrice } from "../components/guest-deals/GuestDealsUi.utils";
import { getPublicDealPreview, getZipFromCoordinates } from "../services/PublicPreviewService";
import type { ProductListItem } from "../types/Product";
import { track } from "../utils/analytics";
import { buildGetStartedUrl, buildGuestDealAuthReturnTo } from "../utils/authReturn";
import { useAuthModal } from "../hooks/useAuthModal";
import { BASIC_INTRO_LABEL, BASIC_PRICE_LABEL } from "../utils/basicPlanOffer";
import RetailerPicker from "../components/home-feed/RetailerPicker";
import HomeLanding from "../components/home-feed/HomeLanding";
import { OfferStrip } from "../components/home-feed/HomePromos";
import GlobalSearchResults from "../components/home-feed/GlobalSearchResults";
import SiteFooter from "../components/home-feed/SiteFooter";
import { ActiveFilterChips, FeedFilterSheet, FeedFilterSidebar, FiltersIcon } from "../components/home-feed/FeedFilters";
import {
  FEED_SORT_LABELS,
  FEED_SORT_REQUEST,
  activeFilterCount,
  cleanPrice,
  isFeedSort,
  isFeedStatus,
  priceLabel,
  type FeedFilterHandlers,
  type FeedFilterValues,
  type FeedSort,
  type FeedStatus,
} from "../components/home-feed/feedFilterLogic";
import DealDetailModal, { type DealGateAction } from "../components/home-feed/DealDetailModal";
import DealRow, { ViewAllButton, categoryTitle } from "../components/home-feed/DealRow";
import { StoreRail, StoreStatusChips } from "../components/home-feed/StoreRail";
import { EXAMPLE_PREVIEW_ZIP, dedupeByLook, orderCategoryRows, prioritizeNonApparel } from "../components/home-feed/homeFeedLogic";
import {
  ChevronDownIcon,
  FeedSkeletonCard,
  FeedSkeletonRow,
  HF,
  HomeFeedShareContext,
  LockIcon,
  LockedDealCard,
  LockedDealRow,
  LockedSkeletonCard,
  ProductFeedCard,
  ProductFeedRow,
  SearchIcon,
  MoreInstoreCard,
  ZipPromptCard,
  InstoreEmptyState,
  localChainName,
  retailerLabel,
} from "../components/home-feed/homeFeedUi";
import AllStores from "../components/home-feed/AllStores";
import ClearanceView from "../components/home-feed/ClearanceView";
import { FreeMemberHeaderActions, freeUpgradeUrl, trackFreeUpgrade, useHomeFeedViewer } from "../components/home-feed/viewer";

/**
 * Logged-out home ("/"): the app's online deals feed, light mode, with locked
 * in-store clearance cards that sell the paid plan. Opening anything needs an
 * account. Shares /deals' public browse query (same key shape) so the two
 * pages reuse each other's cache.
 */

export const GUEST_ZIP_KEY = "dealhawk_guest_zip";
const GEO_PROMPTED_KEY = "dealhawk_guest_geo_prompted";
const PAGE_SIZE = 36;
const SOURCE = "home_feed";

type Sort = FeedSort;
const SORT_REQUEST = FEED_SORT_REQUEST;
const STATUS_CHIP: Record<Exclude<FeedStatus, "all">, string> = { new: "New today", price_drop: "Price drops", lowest: "Lowest price" };

/** Locked in-store slots go before product index 2, then every 8 products. */
export function isLockedSlot(productIndex: number): boolean {
  return productIndex === 2 || (productIndex > 2 && (productIndex - 2) % 8 === 0);
}

function readStorage(key: string): string {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage can be unavailable in private browsing */
  }
}

function initialZip(): string {
  const zip = readStorage(GUEST_ZIP_KEY) || readStorage("dealhawk-zip-code");
  return /^\d{5}$/.test(zip) ? zip : "";
}

function Chip({ active, children, className = "" }: { active?: boolean; children: ReactNode; className?: string }) {
  return (
    <span
      className={`relative flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors hover:border-[var(--hf-violet-border)] ${className}`}
      style={
        active
          ? { borderColor: HF.violetBorder, background: HF.violetSoft, color: HF.brandText, fontWeight: 700 }
          : { borderColor: HF.chipBorder, background: HF.surface, color: HF.text2, fontWeight: 600 }
      }
    >
      {children}
    </span>
  );
}




/** Store tabs in the header's second row, in this order; the picker holds the rest. */
const HEADER_TAB_SLUGS = ["walmart", "amazon", "target", "home-depot", "lowes", "best-buy", "costco", "kohls"];
const HEADER_TAB_RETAILERS = HEADER_TAB_SLUGS.map((slug) => getPublicDealsRetailer(slug)).filter(
  (retailer): retailer is PublicDealsRetailer => Boolean(retailer),
);
const HEADER_INK = "#170B26";
const HEADER_GOLD = "#FFD24A";
/** Header fill: brand purple in light mode, the page colour (with a hairline) in dark mode. */
const HEADER_BG = HF.header;
/** Dark page colour, for the browser chrome tint (meta theme-color needs a literal). */
const DARK_PAGE = "#0B0B0D";
/** `--hf-page` under `.dark.dim`, for the browser chrome tint. */
const DIM_PAGE = "#1E1E21";

const STORES_INDEX_SLUG = STORES_INDEX_PATH.slice(1);
const CLEARANCE_SLUG = CLEARANCE_PATH.slice(1);

/** Store pages whose own in-store clearance the public preview can show (public slug → chain id). */
const PREVIEW_CHAINS: Record<string, string> = {
  walmart: "walmart",
  target: "target",
  "best-buy": "bestbuy",
  costco: "costco",
  "home-depot": "home_depot",
};
const PREVIEW_CHAIN_NAMES = ["Walmart", "Target", "Best Buy", "Costco", "HD"];

/** On an online-only store's page: there's no in-store clearance here, so point at the chains that have it. */
function OtherStoresClearance({ storeLabel, to, onClick }: { storeLabel: string; to: string; onClick: () => void }) {
  return (
    <section className="flex flex-col gap-4 rounded-[20px] p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-7" style={{ background: HF.surface2 }} data-testid="row-other-stores-clearance">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h2 className="m-0 text-[18px] font-extrabold tracking-[-0.4px] sm:text-[22px] sm:tracking-[-0.5px]">In-store clearance near you</h2>
        <p className="m-0 text-[14px] leading-[21px] sm:text-[15px] sm:leading-[22px]" style={{ color: HF.body }}>
          {storeLabel} is online only. DealHawk members see clearance at stores near you, with the price, aisle and how many are left.
        </p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {PREVIEW_CHAIN_NAMES.map((name) => (
            <span key={name} className="flex h-7 items-center rounded-full bg-[var(--hf-chip-bg)] px-3 text-[12.5px] font-bold" style={{ color: HF.ink }}>{name}</span>
          ))}
        </div>
      </div>
      <Link
        to={to}
        onClick={onClick}
        className="flex h-12 flex-shrink-0 items-center justify-center rounded-full px-6 text-[15px] font-bold no-underline hover:opacity-90"
        style={{ background: HF.inkBtn, color: HF.onInkBtn }}
      >
        Get 50% off · {BASIC_INTRO_LABEL}
      </Link>
    </section>
  );
}

/**
 * Row requests are paced: the public browse endpoint caps concurrent loads server-wide,
 * so a page that fires a dozen rows at once gets some of them refused.
 */
const ROW_FETCH_CONCURRENCY = 3;
let rowFetchesInFlight = 0;
const rowFetchQueue: Array<() => void> = [];
async function withRowSlot<T>(task: () => Promise<T>): Promise<T> {
  if (rowFetchesInFlight >= ROW_FETCH_CONCURRENCY) {
    await new Promise<void>((resolve) => rowFetchQueue.push(resolve));
  }
  rowFetchesInFlight++;
  try {
    return await task();
  } finally {
    rowFetchesInFlight--;
    rowFetchQueue.shift()?.();
  }
}

/**
 * "/"                    all stores: a "New at …" row per store
 * "/:store"              one store: newest, in-store clearance, category rows
 * "/:store/deals"        one store, every deal (grid)
 * "/:store/:category"    one store, one category (grid)
 */
/** Desktop padding that centers a 1300px column in the space right of the 232px rail (percent = the full-width row). */
const CENTER_BESIDE_RAIL = "lg:px-[max(40px,calc((100%_-_232px_-_1300px)/2))]";

export default function HomeFeedPage() {
  const { isDark, darkStyle } = useTheme();
  usePageBackground(HF.page, isDark ? (darkStyle === "gray" ? DIM_PAGE : DARK_PAGE) : "#3B085F");
  const navigate = useNavigate();
  const { retailerSlug, section } = useParams();
  const routeRetailer = retailerSlug ? getPublicDealsRetailer(retailerSlug) : null;
  // "/stores" lists every online store; it shares the home page's header and default feed.
  const isStoresIndex = retailerSlug === STORES_INDEX_SLUG && !routeRetailer;
  // "/clearance" shares the same header; it shows nearby in-store clearance one store at a time.
  const isClearance = retailerSlug === CLEARANCE_SLUG && !routeRetailer;
  const isAllStores = !retailerSlug || isStoresIndex || isClearance;
  const retailer = routeRetailer || DEFAULT_PUBLIC_DEALS_RETAILER;
  const viewAll = section === "deals";
  const category = section && !viewAll ? section : "";
  useSeo(isClearance ? CLEARANCE_SEO : isAllStores ? HOME_SEO : routeRetailer ? homeFeedSeo(routeRetailer, viewAll ? "deals" : category) : null);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Signed-in free members get this page too (see AppRouter); their unlock links go to the upgrade flow.
  const { freeMember, gateUrl, accountZip } = useHomeFeedViewer();
  const [zip, setZip] = useState(() => initialZip() || accountZip);

  // No ZIP yet: ask the browser for the shopper's location once and turn it into the nearest store's ZIP,
  // so "clearance near you" fills in without typing. A refusal is remembered and never asked again.
  useEffect(() => {
    if (zip || readStorage(GEO_PROMPTED_KEY) || typeof navigator === "undefined" || !navigator.geolocation) return;
    writeStorage(GEO_PROMPTED_KEY, "1");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const found = await getZipFromCoordinates(position.coords.latitude, position.coords.longitude);
        track("guest_location_zip", { surface: "web", retailer: "local", trigger: "geolocation", source: SOURCE, has_zip: Boolean(found) });
        if (found && !readStorage(GUEST_ZIP_KEY)) {
          writeStorage(GUEST_ZIP_KEY, found);
          setZip(found);
        }
      },
      () => track("guest_location_zip", { surface: "web", retailer: "local", trigger: "geolocation_denied", source: SOURCE, has_zip: false }),
      { timeout: 10_000, maximumAge: 60 * 60_000 },
    );
    // Runs once per page visit; the ZIP itself changes through submitZip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pickerOpen, setPickerOpen] = useState(false);
  // The picker drops down under whichever control opened it (the mobile store bar, a rail's "Change store").
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const openPicker = (anchor: HTMLElement) => {
    setPickerAnchor(anchor);
    setPickerOpen(true);
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<{ product: ProductListItem; retailer: PublicDealsRetailer } | null>(null);
  const showSections = !searchTerm && !category && !viewAll;
  /** The all-stores home page (HomeLanding), not a store, search or the stores index. */
  const isLanding = isAllStores && showSections && !isStoresIndex && !isClearance;
  // Home search covers every store's online deals: /?q= swaps the landing for results.
  const globalQuery = isLanding ? (searchParams.get("q") || "").trim().slice(0, 120) : "";
  const [globalInput, setGlobalInput] = useState(() => searchParams.get("q") || "");
  useEffect(() => {
    if (!isLanding) return;
    const timer = window.setTimeout(() => {
      const next = globalInput.trim();
      if (next === globalQuery) return;
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next) params.set("q", next);
          else params.delete("q");
          return params;
        },
        { replace: true },
      );
      if (next) track("global_search", { surface: "web", length: next.length });
    }, 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the typed text schedules a URL update
  }, [globalInput, isLanding]);
  // Back/forward or a link changed ?q=: show it in the box.
  useEffect(() => {
    if (globalQuery !== globalInput.trim()) setGlobalInput(globalQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow the URL, not each keystroke
  }, [globalQuery]);
  const clearGlobalSearch = () => setGlobalInput("");
  const globalSearchBox = (className: string) => (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        (document.activeElement as HTMLElement | null)?.blur();
      }}
      className={`${className} h-11 min-w-0 items-center gap-2.5 rounded-full bg-[var(--hf-surface)] pl-4 pr-1.5 focus-within:ring-2 focus-within:ring-[#FFD24A]`}
    >
      <span style={{ color: HF.muted }}><SearchIcon /></span>
      <input
        type="search"
        enterKeyHint="search"
        aria-label="Search online deals at every store"
        placeholder="Search deals at 60+ stores"
        value={globalInput}
        maxLength={120}
        onChange={(event) => setGlobalInput(event.target.value)}
        className="min-w-0 flex-1 border-0 bg-transparent text-[16px] outline-none placeholder:text-[var(--hf-placeholder)] md:text-[15px] [&::-webkit-search-cancel-button]:appearance-none"
        style={{ color: HF.ink }}
        data-testid="global-search-input"
      />
      {globalInput && (
        <button type="button" onClick={clearGlobalSearch} aria-label="Clear search" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full" style={{ color: HF.muted }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      )}
    </form>
  );

  // Grid filters live in the query string (?sort=&discount=&minPrice=&maxPrice=&status=) so
  // filtered links are shareable; the category is the path. Store links carry no query, so
  // switching stores resets them. The sectioned home ignores them.
  const sortParam = searchParams.get("sort");
  const statusParam = searchParams.get("status");
  const discountParam = searchParams.get("discount");
  const filterValues: FeedFilterValues = {
    // The sectioned home keeps newest-first rows; the grid defaults to Best deal.
    sort: showSections ? "updated" : isFeedSort(sortParam) ? sortParam : "best",
    category,
    status: !showSections && isFeedStatus(statusParam) ? statusParam : "all",
    minDiscount: !showSections && discountParam && /^\d{1,2}$/.test(discountParam) ? discountParam : "",
    minPrice: showSections ? "" : cleanPrice(searchParams.get("minPrice")),
    maxPrice: showSections ? "" : cleanPrice(searchParams.get("maxPrice")),
  };
  const sort: Sort = filterValues.sort;
  const activeFilters = activeFilterCount(filterValues);

  // A new page (store, category, all deals) starts without the previous search,
  // except a category picked from the filters while searching.
  const keepSearchRef = useRef(false);
  useEffect(() => {
    if (keepSearchRef.current) {
      keepSearchRef.current = false;
      return;
    }
    setSearchInput("");
    setSearchTerm("");
  }, [retailerSlug, section]);
  const [layout, setLayout] = useLayoutMode("guestFeed");
  const pickerButtonRef = useRef<HTMLButtonElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [stickyHeight, setStickyHeight] = useState(146);
  useLayoutEffect(() => {
    const target = stickyRef.current;
    if (!target) return;
    const measure = () => setStickyHeight(target.offsetHeight || 146);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    return () => observer.disconnect();
  }, []);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchTerm(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    track("guest_deals_viewed", { surface: "web", retailer: retailer.slug, source: SOURCE });
  }, [retailer.slug]);

  // Same request shape as the deal pages so they share the query cache.
  const { status: filterStatus, minDiscount, minPrice, maxPrice } = filterValues;
  const requestFilters = useMemo(
    () => ({
      searchTokens: searchTerm.toLowerCase().split(/\s+/).filter(Boolean).slice(0, 8),
      categories: category ? [category] : [],
      status: filterStatus,
      minDiscount: minDiscount ? Number(minDiscount) : undefined,
      minStorePrice: minPrice ? Number(minPrice) : undefined,
      maxStorePrice: maxPrice ? Number(maxPrice) : undefined,
      ...SORT_REQUEST[sort],
    }),
    [category, filterStatus, maxPrice, minDiscount, minPrice, searchTerm, sort],
  );

  const query = useInfiniteQuery({
    queryKey: ["public-products-browse", retailer.apiRetailer, requestFilters],
    queryFn: ({ pageParam }) =>
      browsePublicProducts({
        retailer: retailer.apiRetailer,
        storeId: retailer.storeId,
        ...requestFilters,
        limit: PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    enabled: !(isAllStores && !searchTerm),
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const totalDeals = query.data?.pages[0]?.total ?? 0;
  const products = useMemo(() => {
    const seen = new Set<string>();
    const result: ProductListItem[] = [];
    for (const product of query.data?.pages.flatMap((page) => page.products || []) || []) {
      const key = `${product.productKey || product.upc || product.sku}:${product.storeId || "online"}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(product);
      }
    }
    return dedupeByLook(result);
  }, [query.data]);
  const categories = query.data?.pages[0]?.categoryCounts || [];
  const topCategories = useMemo(() => orderCategoryRows(categories), [categories]);
  // Every department for the store rail, biggest first.
  const allCategoryRows = useMemo(() => orderCategoryRows(categories, 12), [categories]);
  const statusCounts = query.data?.pages[0]?.statusCounts;

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;
    // The app shell scrolls inside its own container, not the window; a viewport root would clip
    // the margin away and only load the next page once the footer is already on screen.
    let scroller: HTMLElement | null = target.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) void fetchNextPage();
      },
      { root: scroller, rootMargin: "1200px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, showSections]);

  // Which chain's in-store clearance to tease: Walmart on the home page, the store itself on a store
  // page, and none for online-only stores (they get OtherStoresClearance instead).
  const previewChain = isAllStores ? "walmart" : PREVIEW_CHAINS[retailer.slug] ?? null;
  const preview = useQuery({
    queryKey: ["public-deal-preview", zip, previewChain],
    enabled: /^\d{5}$/.test(zip) && previewChain !== null,
    staleTime: 10 * 60_000,
    retry: 1,
    queryFn: async () => {
      track("guest_local_preview_started", { surface: "web", retailer: "local", trigger: "zip", source: SOURCE });
      try {
        const result = await getPublicDealPreview(zip, previewChain ?? "walmart");
        track("guest_local_preview_results", {
          surface: "web",
          retailer: "local",
          trigger: "zip",
          source: SOURCE,
          deal_count: result.deals.length,
          stores_scanned: result.storesScanned,
          has_deals: result.deals.length > 0,
        });
        return result;
      } catch (error) {
        track("guest_local_preview_failed", { surface: "web", retailer: "local", trigger: "zip", source: SOURCE, reason: "request_failed" });
        throw error;
      }
    },
  });

  // Real finds from another area, labelled as examples, for shoppers whose own ZIP is unknown or has nothing.
  const needsExamples = !zip || (preview.isSuccess && (preview.data?.deals.length || 0) === 0);
  const examplePreview = useQuery({
    queryKey: ["public-deal-preview", EXAMPLE_PREVIEW_ZIP, previewChain],
    enabled: needsExamples && previewChain !== null,
    staleTime: 30 * 60_000,
    retry: 1,
    queryFn: () => getPublicDealPreview(EXAMPLE_PREVIEW_ZIP, previewChain ?? "walmart"),
  });

  const dealsPath = getPublicDealsPath(retailer.slug);
  const localSignupUrl = useCallback(
    (source: string) => gateUrl(buildGetStartedUrl(buildGuestDealAuthReturnTo("/deals", "local"), source), source),
    // gateUrl only changes with freeMember.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [freeMember],
  );
  const signupUrl = buildGetStartedUrl("", "home_feed_signup");
  const loginUrl = buildGetStartedUrl("", "home_feed_login");

  const trackSignup = useCallback(
    (trigger: string) =>
      freeMember
        ? trackFreeUpgrade(trigger, retailer.slug)
        : track("guest_signup_started", { surface: "web", retailer: retailer.slug, trigger, source: SOURCE }),
    [freeMember, retailer.slug],
  );

  // Sign in / sign up opens over the page instead of leaving for /get-started.
  const auth = useAuthModal();

  // Share sheet for a feed card: the deal's /p/ affiliate link, so every
  // shared deal can earn a commission.
  const [shareTarget, setShareTarget] = useState<{ product: ProductListItem; storeLabel: string } | null>(null);
  const shareDeal = useCallback((product: ProductListItem, storeLabel: string) => {
    track("deal_share_opened", { surface: "web", retailer: retailer.slug, source: SOURCE });
    setShareTarget({ product, storeLabel });
  }, [retailer.slug]);
  const sharePriceLine = (() => {
    if (!shareTarget) return "";
    const price = getGuestDealPrice(shareTarget.product);
    const discount = getGuestDealDiscount(shareTarget.product);
    const amount = price > 0 ? `$${price.toFixed(2)}` : "";
    return [amount, discount > 0 ? `(${discount}% off)` : "", `at ${shareTarget.storeLabel}`].filter(Boolean).join(" ");
  })();

  const openDetail = (product: ProductListItem, from: PublicDealsRetailer = retailer) => {
    track("guest_deal_opened", { surface: "web", retailer: from.slug, trigger: "deal_card", source: SOURCE });
    if (product.productKey) {
      navigate(homeDealPath(from, product.productKey), { state: { product } });
      window.scrollTo({ top: 0 });
      return;
    }
    setSelected({ product, retailer: from });
  };

  const gateFromDetail = (product: ProductListItem, action: DealGateAction) => {
    track("guest_signup_prompt_viewed", { surface: "web", retailer: retailer.slug, trigger: `detail_${action}`, source: SOURCE });
    setSelected(null);
    if (freeMember) navigate(freeUpgradeUrl(`home_feed_detail_${action}`));
    else auth.openAuth(buildGuestDealAuthReturnTo(dealsPath, action, product.productKey), `home_feed_detail_${action}`);
  };

  const goTo = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0 });
  };
  const storePath = (r: PublicDealsRetailer, rest = "") => `/${r.slug}${rest ? `/${encodeURIComponent(rest)}` : ""}`;

  const backToSections = () => {
    setSearchInput("");
    setSearchTerm("");
    if (category || viewAll) goTo(isAllStores ? "/" : storePath(retailer));
  };

  const openCategory = (id: string) => goTo(storePath(retailer, id));

  // ---- grid filters ----
  const scrollToGridTop = () => {
    if (!filtersOpen && window.scrollY > 240) window.scrollTo({ top: 0 });
  };
  const setFilterParams = useCallback(
    (patch: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const selectCategory = (id: string) => {
    if (id === category) return;
    // Categories are real pages (/:store/:category) so they stay crawlable; other filters ride along.
    keepSearchRef.current = Boolean(searchTerm);
    navigate(`${storePath(retailer, id || "deals")}${location.search}`);
    scrollToGridTop();
  };
  const onPrice = useCallback(
    (min: string, max: string) => setFilterParams({ minPrice: min, maxPrice: max }),
    [setFilterParams],
  );
  const filterHandlers: FeedFilterHandlers = {
    onSort: (value) => {
      setFilterParams({ sort: value === "best" ? "" : value });
      scrollToGridTop();
    },
    onCategory: selectCategory,
    onStatus: (value) => {
      setFilterParams({ status: value === "all" ? "" : value });
      scrollToGridTop();
    },
    onDiscount: (value) => {
      setFilterParams({ discount: value });
      scrollToGridTop();
    },
    onPrice,
  };
  const clearAllFilters = () => {
    if (category) {
      keepSearchRef.current = Boolean(searchTerm);
      const next = new URLSearchParams(location.search);
      for (const key of ["discount", "minPrice", "maxPrice", "status"]) next.delete(key);
      const query = next.toString();
      navigate(`${storePath(retailer, "deals")}${query ? `?${query}` : ""}`);
    } else {
      setFilterParams({ discount: "", minPrice: "", maxPrice: "", status: "" });
    }
  };
  const filterChips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  if (category) filterChips.push({ key: "category", label: categoryTitle(category), onRemove: () => selectCategory("") });
  if (filterValues.status !== "all") {
    filterChips.push({ key: "status", label: STATUS_CHIP[filterValues.status], onRemove: () => filterHandlers.onStatus("all") });
  }
  if (minDiscount) filterChips.push({ key: "discount", label: `${minDiscount}%+ off`, onRemove: () => filterHandlers.onDiscount("") });
  if (minPrice || maxPrice) {
    filterChips.push({ key: "price", label: priceLabel(minPrice, maxPrice), onRemove: () => setFilterParams({ minPrice: "", maxPrice: "" }) });
  }

  const openProduct = (product: ProductListItem, action: "history" | "save") => {
    track(action === "save" ? "guest_signup_prompt_viewed" : "guest_deal_opened", {
      surface: "web",
      retailer: retailer.slug,
      trigger: action === "save" ? "save" : "deal_card",
      source: SOURCE,
    });
    const source = action === "save" ? "home_feed_save" : "home_feed_card";
    if (freeMember) navigate(freeUpgradeUrl(source));
    else auth.openAuth(buildGuestDealAuthReturnTo(dealsPath, action, product.productKey), source);
  };

  const selectRetailer = (next: PublicDealsRetailer, trigger = "retailer_picker") => {
    setPickerOpen(false);
    if (!isAllStores && next.slug === retailer.slug && !section) return;
    track("guest_retailer_selected", { surface: "web", retailer: next.slug, trigger, source: SOURCE });
    goTo(storePath(next));
  };
  const closePicker = useCallback(() => setPickerOpen(false), []);

  const tabRetailers = isAllStores || HEADER_TAB_RETAILERS.some((r) => r.slug === retailer.slug)
    ? HEADER_TAB_RETAILERS
    : [retailer, ...HEADER_TAB_RETAILERS];
  // Store tabs only; the logo is the way home.
  const headerTabs = tabRetailers.map((r) => ({ key: r.slug, label: r.displayName ?? r.shortName, to: storePath(r), retailer: r, active: !isAllStores && r.slug === retailer.slug }));

  // Header "Your stores": jump to the clearance-near-you section of the home page.
  const showYourStores = () => {
    const scrollToClearance = () => document.getElementById("clearance-near-you")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (isAllStores && showSections && !isClearance) scrollToClearance();
    else {
      navigate("/");
      window.setTimeout(scrollToClearance, 350);
    }
  };

  const submitZip = (value: string) => {
    writeStorage(GUEST_ZIP_KEY, value);
    setZip(value);
  };

  const renderLockedSlot = (slot: number) => {
    if (!zip) {
      return slot === 0 ? <ZipPromptCard introLabel={BASIC_INTRO_LABEL} onSubmit={submitZip} /> : null;
    }
    if (preview.isLoading) return slot === 0 ? <LockedSkeletonCard /> : null;
    if (preview.isError) {
      return slot === 0 ? (
        <ZipPromptCard
          key={`zip-error-${zip}`}
          initialZip={zip}
          error="Couldn’t check that ZIP. Try again."
          introLabel={BASIC_INTRO_LABEL}
          onSubmit={(value) => (value === zip ? void preview.refetch() : submitZip(value))}
        />
      ) : null;
    }
    const deal = prioritizeNonApparel(preview.data?.deals || [])[slot];
    if (!deal) return null;
    return (
      <LockedDealCard
        deal={deal}
        zip={zip}
        to={localSignupUrl("home_feed_locked_card")}
        introLabel={BASIC_INTRO_LABEL}
        onClick={() => trackSignup("locked_card")}
      />
    );
  };

  // List layout: only real locked deals become rows; the ZIP prompt and skeletons stay grid-only.
  const renderLockedRow = (slot: number) => {
    if (!zip || preview.isLoading || preview.isError) return null;
    const deal = prioritizeNonApparel(preview.data?.deals || [])[slot];
    if (!deal) return null;
    return (
      <LockedDealRow
        deal={deal}
        zip={zip}
        to={localSignupUrl("home_feed_locked_row")}
        introLabel={BASIC_INTRO_LABEL}
        onClick={() => trackSignup("locked_list_row")}
      />
    );
  };

  const exampleCards = (): ReactNode[] =>
    prioritizeNonApparel(examplePreview.data?.deals || []).map((deal, index) => (
      <LockedDealCard
        key={`example-${deal.name}-${index}`}
        deal={deal}
        zip={zip}
        example
        to={localSignupUrl("home_feed_example_card")}
        introLabel={BASIC_INTRO_LABEL}
        onClick={() => trackSignup("example_card")}
      />
    ));

  // A ZIP is set but this chain has nothing there (or the check failed): the row shows one calm,
  // full-width empty state instead of a lone ZIP card, with any example finds after it.
  const zipEmpty = Boolean(zip) && (preview.isError || (preview.isSuccess && preview.data.deals.length === 0));

  const lockedRowItems = (): ReactNode[] => {
    if (!zip) return [<ZipPromptCard key="zip" introLabel={BASIC_INTRO_LABEL} onSubmit={submitZip} />, ...exampleCards()];
    if (preview.isLoading) return [0, 1, 2].map((i) => <LockedSkeletonCard key={i} />);
    if (zipEmpty) return exampleCards();
    const deals = prioritizeNonApparel(preview.data?.deals || []);
    if (deals.length === 0) return [];
    return [...deals.map((deal, index) => (
      <LockedDealCard
        key={`${deal.name}-${index}`}
        deal={deal}
        zip={zip}
        to={localSignupUrl("home_feed_locked_card")}
        introLabel={BASIC_INTRO_LABEL}
        onClick={() => trackSignup("locked_card")}
      />
    )), <MoreInstoreCard key="more" zip={zip} to={localSignupUrl("home_feed_more_instore")} onClick={() => trackSignup("more_instore")} />];
  };

  const label = retailerLabel(retailer);
  let lockedSlot = 0;

  // The dark "in store near you" pitch at the bottom of the store rail (sections and grid).
  const railInstore = {
    eyebrow: zip ? `IN STORE NEAR ${zip}` : "IN STORE NEAR YOU",
    title: previewChain ? `Clearance up to 70% off at your ${localChainName(previewChain)}` : "Clearance up to 70% off at Walmart, Target and more",
    to: localSignupUrl("store_rail_instore"),
    onClick: () => trackSignup("store_rail_instore"),
  };
  const chainName = previewChain ? localChainName(previewChain) : "";
  const rowItems = previewChain ? lockedRowItems() : [];
  const zipEmptyChain = isAllStores ? "" : `${chainName} `;
  const instoreRow = !previewChain ? (
    <OtherStoresClearance
      storeLabel={label}
      to={localSignupUrl("home_feed_other_stores")}
      onClick={() => trackSignup("other_stores_clearance")}
    />
  ) : zipEmpty ? (
    <DealRow
      title={isAllStores ? `Clearance near ${zip}` : `${chainName} clearance near ${zip}`}
      meta={rowItems.length > 0 ? `Example finds from ${isAllStores ? "other stores" : `another ${chainName}`}` : undefined}
      lead={
        <InstoreEmptyState
          key={`instore-empty-${zip}`}
          title={
            preview.isError
              ? `Couldn’t check ${zipEmptyChain}clearance near ${zip} right now`
              : `No ${zipEmptyChain}clearance found near ${zip} yet`
          }
          message={
            preview.isError
              ? "Try again in a moment, or try a nearby ZIP."
              : "We check stores every day. Try a nearby ZIP, or see what members find at other stores."
          }
          initialZip={zip}
          plansTo={localSignupUrl("home_feed_instore_empty")}
          onPlansClick={() => trackSignup("instore_empty")}
          onSubmit={(value) => (value === zip ? void preview.refetch() : submitZip(value))}
        />
      }
      testId="row-instore"
    >
      {rowItems}
    </DealRow>
  ) : rowItems.length > 0 && (
              <DealRow
                title={
                  zip && !needsExamples
                    ? isAllStores ? `Clearance near ${zip}` : `${chainName} clearance near ${zip}`
                    : isAllStores ? "In-store clearance near you" : `In-store clearance at ${chainName}`
                }
                meta={needsExamples ? `Example finds from ${isAllStores ? "other stores" : `another ${chainName}`}. Add your ZIP for yours.` : undefined}
                icon={<span className="flex h-7 w-7 items-center justify-center rounded-full" style={{ background: HF.amberSoft }}><LockIcon size={14} color={HF.amberText} /></span>}
                action={
                  <Link
                    to={localSignupUrl("home_feed_locked_row")}
                    onClick={() => trackSignup("locked_row")}
                    className="flex h-9 flex-shrink-0 items-center gap-1 rounded-full px-3.5 text-[13px] font-extrabold no-underline transition hover:brightness-95 sm:h-10 sm:px-4 sm:text-[13.5px]"
                    style={{ background: HEADER_GOLD, color: HEADER_INK }}
                  >
                    <span className="hidden sm:inline">Unlock deals ·</span>{BASIC_INTRO_LABEL}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </Link>
                }
                testId="row-instore"
              >
                {rowItems}
              </DealRow>
            );

  if (retailerSlug && !routeRetailer && !isStoresIndex && !isClearance) return <NotFoundPage />;

  return (
    <HomeFeedShareContext.Provider value={shareDeal}>
    <div className="min-h-full" style={{ background: HF.page, color: HF.ink, fontFamily: HF.font, colorScheme: "var(--hf-scheme)" }} onClickCapture={auth.interceptAuthLinks}>
      {isLanding && (
        <OfferStrip to={localSignupUrl("home_offer_strip")} onClick={() => trackSignup("offer_strip")} upgrade={freeMember} />
      )}
      <div ref={stickyRef} className="sticky top-0 z-40 shadow-[0_1px_0_var(--hf-shadow-sm)]">
      <header className="border-b text-white" style={{ background: HEADER_BG, borderColor: "var(--hf-header-border)" }}>
        <div className="mx-auto flex h-14 max-w-[1360px] items-center gap-1.5 px-4 sm:h-16 sm:gap-3 sm:px-5 lg:max-w-none lg:gap-6 lg:px-6">
          <Link to="/" aria-label="DealHawk home" className="flex flex-shrink-0 items-center gap-2 text-white no-underline">
            <img src="/hawk_icon_white.svg" alt="" width={30} height={30} className="h-[28px] w-[28px] object-contain sm:h-[30px] sm:w-[30px]" />
            <span className="text-[18px] font-extrabold tracking-[-0.4px] sm:text-[19px]">DealHawk</span>
          </Link>
          {isLanding ? globalSearchBox("ml-2 hidden max-w-[560px] flex-1 md:flex lg:ml-6") : isAllStores ? <span className="hidden flex-1 md:block" /> : (
          <label className="hidden h-11 min-w-0 max-w-[560px] flex-1 items-center gap-2.5 rounded-full border border-[var(--hf-header-border)] bg-[var(--hf-surface)] pl-[18px] pr-1.5 focus-within:ring-2 focus-within:ring-[#FFD24A] md:flex">
            <input
              type="search"
              aria-label={`Search ${isAllStores ? "" : `${label} `}deals`}
              placeholder={isAllStores ? "What are you looking for?" : `Search ${label} deals`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent text-[15px] outline-none placeholder:text-[var(--hf-placeholder)]"
              style={{ color: HF.ink }}
            />
            <span className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full" style={{ background: HEADER_GOLD, color: HEADER_INK }} aria-hidden="true">
              <SearchIcon />
            </span>
          </label>
          )}
          <span className="flex-1" />
          <Link
            to={CLEARANCE_PATH}
            onClick={() => track("clearance_nav_tapped", { surface: "web", source: SOURCE, signed_in: freeMember })}
            aria-current={isClearance ? "page" : undefined}
            aria-label="Clearance"
            className="flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-sm font-semibold no-underline transition-colors hover:bg-white/[0.12] sm:px-3 lg:h-[46px] lg:px-4 lg:text-[15px]"
            style={isClearance ? { color: "#FFFFFF", background: "rgba(255,255,255,0.14)" } : { color: "#FFFFFF" }}
            data-testid="header-clearance"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill={isClearance ? HEADER_GOLD : "none"} stroke={HEADER_GOLD} strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M3 12V4h8l10 10-8 8z" /><circle cx="7.5" cy="8" r="1.5" fill={isClearance ? HEADER_BG : "none"} /></svg>
            <span className="hidden sm:inline">Clearance</span>
          </Link>
          <button
            type="button"
            onClick={showYourStores}
            className="hidden h-[46px] flex-shrink-0 items-center gap-2.5 rounded-full border pl-3.5 pr-[18px] text-left text-[13px] leading-4 text-white transition-colors hover:bg-white/[0.14] lg:flex"
            style={{ borderColor: "rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.08)" }}
            data-testid="header-your-stores"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={HEADER_GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>
            <span>
              <span className="block text-[12px]" style={{ color: "var(--hf-header-muted)" }}>Shopping near</span>
              <strong className="text-[15px] font-semibold">{zip || "Set your ZIP"}</strong>
            </span>
          </button>
          {freeMember ? (
            <FreeMemberHeaderActions source="home_feed" retailer={retailer.slug} ink={HEADER_INK} gold={HEADER_GOLD} />
          ) : (
            <>
              <Link
                to={loginUrl}
                onClick={() => track("guest_signin_started", { surface: "web", retailer: retailer.slug, trigger: "header", source: SOURCE })}
                className="whitespace-nowrap px-1.5 py-2 text-sm font-semibold text-white no-underline hover:text-[#E9E0F4]"
              >
                Sign in
              </Link>
              <Link
                to={signupUrl}
                onClick={() => trackSignup("header")}
                className="flex h-9 flex-shrink-0 items-center whitespace-nowrap rounded-full bg-white px-4 text-sm font-bold no-underline hover:bg-[#F1ECF8] sm:h-10 sm:px-5 lg:h-[46px] lg:px-6 lg:text-[15px]"
                style={{ color: "#3A0A63" }}
              >
                Join
              </Link>
            </>
          )}
        </div>
        {/* Store tabs are a phone/tablet thing; on desktop the left rail lists and switches stores. */}
        <div className={isLanding || isClearance ? "hidden" : "border-t lg:hidden"} style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="mx-auto flex h-[42px] max-w-[1360px] items-stretch pl-4 sm:h-[46px] sm:pl-5 lg:max-w-none lg:pl-6">
            <nav aria-label="Stores" className="scrollbar-hide flex min-w-0 flex-1 items-stretch gap-5 overflow-x-auto whitespace-nowrap text-[13.5px] font-semibold sm:gap-6 sm:text-sm">
              {headerTabs.map((tab) => (
                <Link
                  key={tab.key}
                  to={tab.to}
                  onClick={(event) => {
                    event.preventDefault();
                    selectRetailer(tab.retailer, "header_tab");
                  }}
                  aria-current={tab.active ? "page" : undefined}
                  className="flex flex-shrink-0 items-center no-underline transition-colors hover:text-white"
                  style={tab.active ? { color: "#FFFFFF", boxShadow: `inset 0 -2px 0 ${HEADER_GOLD}` } : { color: "var(--hf-header-muted)" }}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
            <span className="pointer-events-none -ml-8 w-8 flex-shrink-0" style={{ background: `linear-gradient(to right, transparent, ${HEADER_BG})` }} aria-hidden="true" />
            <button
              ref={pickerButtonRef}
              type="button"
              onClick={(event) => (pickerOpen ? setPickerOpen(false) : openPicker(event.currentTarget))}
              aria-haspopup="dialog"
              aria-expanded={pickerOpen}
              aria-label={`Store: ${isAllStores ? "All stores" : label}. Change store`}
              className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap border-l pl-3 pr-4 text-[13.5px] font-semibold transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60 sm:pl-4 sm:pr-5 sm:text-sm"
              style={{ borderColor: "rgba(255,255,255,0.08)", color: pickerOpen ? "#FFFFFF" : "var(--hf-header-muted)", background: HEADER_BG }}
            >
              More stores
              <ChevronDownIcon size={14} up={pickerOpen} />
            </button>
          </div>
        </div>
      </header>

      <div className={showSections ? "hidden" : "lg:hidden"} style={{ background: HF.page, borderBottom: `1px solid ${HF.border}` }}>
        <div className="mx-auto flex max-w-[1360px] items-center gap-2 px-4 py-2.5 sm:px-5 lg:max-w-none lg:px-6">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-label={activeFilters > 0 ? `Filters, ${activeFilters} active` : "Filters"}
            className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors hover:border-[var(--hf-violet-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C4B5FD]"
            style={activeFilters > 0 ? { borderColor: HF.violetBorder, background: HF.violetSoft, color: HF.brandText } : { borderColor: HF.chipBorder, color: HF.text2 }}
          >
            <FiltersIcon />
            Filters
            {activeFilters > 0 && (
              <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold tabular-nums text-white" style={{ background: HF.brand }} aria-hidden="true">
                {activeFilters}
              </span>
            )}
          </button>
          <Chip className="min-w-0 pr-2 focus-within:ring-2 focus-within:ring-[#C4B5FD]">
            <select
              aria-label="Sort deals"
              value={sort}
              onChange={(e) => filterHandlers.onSort(e.target.value as Sort)}
              className="min-w-0 cursor-pointer appearance-none truncate bg-transparent pr-5 outline-none"
            >
              {(Object.keys(FEED_SORT_LABELS) as Sort[]).map((value) => (
                <option key={value} value={value}>{FEED_SORT_LABELS[value]}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2.5"><ChevronDownIcon size={14} /></span>
          </Chip>
        </div>
      </div>
      </div>

      {/* Home, phone: search every store, scrolling away under the sticky header. ZIP lives in the hero. */}
      {isLanding && (
        <div className="px-4 pb-3.5 sm:px-5 md:hidden" style={{ background: HEADER_BG }}>
          {globalSearchBox("flex")}
        </div>
      )}

      {/* Instacart layout on desktop: the rail sits on the window's left edge and the content centers in the rest.
          Only the stores index keeps the plain centered column. */}
      <main
        className={
          isAllStores && showSections && !isStoresIndex && !isClearance
            ? "pb-12"
            : isStoresIndex || isClearance
              ? "mx-auto max-w-[1360px] px-3 pb-12 pt-3 sm:px-5 sm:pt-5"
              : "mx-auto max-w-[1360px] px-3 pb-12 pt-3 sm:px-5 sm:pt-5 lg:max-w-none lg:px-0 lg:pt-0"
        }
      >
        <Link
          to={localSignupUrl("home_feed_locked_pill")}
          onClick={() => trackSignup("locked_pill")}
          data-testid="instore-banner"
          className={isAllStores && showSections ? "hidden" : `mb-4 flex h-11 items-center gap-2.5 rounded-xl pl-3.5 pr-1.5 text-[13px] no-underline${isAllStores ? "" : " lg:hidden"}`}
          style={{ background: "var(--hf-promo)", color: "#FFE7A3" }}
          aria-label={`In-store clearance near you is 70% or more off. Unlock for ${BASIC_INTRO_LABEL} first month, then ${BASIC_PRICE_LABEL} per month.`}
        >
          <span className="h-[7px] w-[7px] flex-shrink-0 rounded-full" style={{ background: HEADER_GOLD, boxShadow: "0 0 0 4px rgba(255,210,74,0.18)" }} aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">
            <strong className="font-bold text-white">70%+ off</strong> clearance near you
            <span className="hidden sm:inline" style={{ color: "#CDBDE0" }}> · then {BASIC_PRICE_LABEL}/mo</span>
          </span>
          <span className="flex h-8 flex-shrink-0 items-center rounded-[9px] px-2.5 text-[12px] font-extrabold" style={{ background: HEADER_GOLD, color: HF.ink }}>
            {BASIC_INTRO_LABEL} first month
          </span>
        </Link>
        <div className={showSections ? undefined : "lg:flex lg:items-start"}>
        {!showSections && (
          <FeedFilterSidebar
            top={stickyHeight}
            values={filterValues}
            handlers={filterHandlers}
            categories={categories}
            statusCounts={statusCounts}
            activeCount={activeFilters}
            onClearAll={clearAllFilters}
            storeLabel={isAllStores ? undefined : label}
          />
        )}
        <div className={showSections ? "min-w-0 flex-1" : `min-w-0 flex-1 lg:pt-6 ${CENTER_BESIDE_RAIL}`}>
        <div className={showSections ? "mb-4 flex items-center px-1 sm:mb-5 md:mb-0" : "mb-4 flex flex-wrap items-center gap-x-3 gap-y-2.5 px-1 sm:mb-5"}>
          {!showSections && (
            <button
              type="button"
              onClick={backToSections}
              aria-label="Back to all sections"
              className="-ml-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition hover:bg-[var(--hf-hover-strong)]"
              style={{ color: HF.text2 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
          )}
          {!(isAllStores && showSections) && (
          <h1 className={showSections ? "sr-only" : "min-w-0 truncate text-[20px] font-bold tracking-[-0.4px] sm:text-[24px]"}>
              {showSections
                ? isAllStores ? "Today’s online markdowns" : `${label} deals`
                : searchTerm
                  ? `Results for “${searchTerm}”`
                  : category
                    ? `${categoryTitle(category)} at ${label}`
                    : `All ${label} deals`}
              <span className="sr-only"> and in-store clearance near you</span>
            </h1>
          )}
          {!showSections && (
            <button
              type="button"
              onClick={(event) => openPicker(event.currentTarget)}
              className="flex-shrink-0 text-[13.5px] font-semibold underline-offset-2 hover:underline"
              style={{ color: HF.store }}
            >
              {isAllStores ? "Pick a store" : "Change store"}
            </button>
          )}
          {totalDeals > 0 && !showSections && (
            <span className="flex-shrink-0 text-[13px] font-medium tabular-nums" style={{ color: HF.text3 }}>
              {totalDeals.toLocaleString()} deals
            </span>
          )}
          {!showSections && <span className="flex-1 sm:hidden" />}
          {!showSections && <LayoutToggle mode={layout} onChange={setLayout} />}
          <span className="hidden flex-1 sm:block" />
          {!isAllStores && (
          <label
            className="flex h-10 w-full min-w-0 items-center gap-2 rounded-full border px-3.5 focus-within:border-[#C4B5FD] focus-within:ring-2 focus-within:ring-[var(--hf-violet-soft)] sm:w-[280px] md:hidden"
            style={{ borderColor: HF.chipBorder, color: HF.muted }}
          >
            <SearchIcon />
            <input
              type="search"
              aria-label={`Search ${isAllStores ? "" : `${label} `}deals`}
              placeholder={isAllStores ? "Search deals" : `Search ${label}`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent text-base outline-none sm:text-sm"
              style={{ color: HF.ink }}
            />
          </label>
          )}
        </div>
        {!showSections && <ActiveFilterChips chips={filterChips} onClearAll={clearAllFilters} />}
        {!showSections && !isAllStores && (
          <div className="mb-4 hidden lg:block">
            <StoreStatusChips counts={statusCounts} active={filterValues.status} onStatus={filterHandlers.onStatus} />
          </div>
        )}
        {query.isError && products.length === 0 ? (
          <div className="mx-auto mt-6 max-w-md rounded-[18px] border bg-[var(--hf-surface)] px-5 py-10 text-center" style={{ borderColor: HF.border }} role="alert">
            <p className="text-base font-bold">We couldn’t load {label} deals</p>
            <p className="mt-1 text-sm" style={{ color: HF.text3 }}>The feed may be refreshing. Try again or pick another store.</p>
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="mt-4 h-[38px] rounded-full px-5 text-sm font-bold text-white"
              style={{ background: HF.brand }}
            >
              Try again
            </button>
          </div>
        ) : showSections && isStoresIndex ? (
          <AllStores />
        ) : showSections && isClearance ? (
          <ClearanceView zip={zip} onSubmitZip={submitZip} unlockUrl={localSignupUrl} onUnlock={trackSignup} freeMember={freeMember} />
        ) : showSections && isAllStores && globalQuery ? (
          <GlobalSearchResults query={globalQuery} onOpenDeal={(product, store) => openDetail(product, store)} onClear={clearGlobalSearch} />
        ) : showSections && isAllStores ? (
          <HomeLanding
            zip={zip}
            previewDeals={preview.data?.deals || []}
            previewLoading={Boolean(zip) && preview.isLoading}
            exampleDeals={examplePreview.data?.deals || []}
            onSubmitZip={submitZip}
            onOpenDeal={(product, store) => openDetail(product, store)}
            localSignupUrl={localSignupUrl}
            onSignup={trackSignup}
            freeMember={freeMember}
          />
        ) : showSections && !query.isLoading && products.length > 0 ? (
          <div className="lg:flex lg:items-start">
          <StoreRail
            top={stickyHeight}
            onChangeStore={openPicker}
            label={label}
            totalDeals={totalDeals}
            categories={allCategoryRows}
            onCategory={openCategory}
            instore={railInstore}
          />
          <div className={`flex min-w-0 flex-1 flex-col gap-5 sm:gap-6 lg:pt-6 ${CENTER_BESIDE_RAIL}`}>
          <StoreStatusChips
            counts={statusCounts}
            onStatus={(status) => goTo(`${storePath(retailer, "deals")}?status=${status}`)}
          />
          <div className="flex flex-col gap-6 sm:gap-7 [&>*+*]:border-t [&>*+*]:border-[var(--hf-divider)] [&>*+*]:pt-6">
            <LazyDealRow
              retailer={retailer}
              title="Biggest drops right now"
              sort="discount"
              eager
              onOpen={(product) => openDetail(product)}
              onSave={(product) => openProduct(product, "save")}
              onViewAll={() => goTo(`${storePath(retailer, "deals")}?sort=discount`)}
              testId="row-biggest-drops"
            />
            <DealRow
              title={`Newest at ${label}`}
              meta={totalDeals > 0 ? `${totalDeals.toLocaleString()} deals` : undefined}
              action={<ViewAllButton onClick={() => goTo(storePath(retailer, "deals"))} />}
              testId="row-newest"
            >
              {products.slice(0, 12).map((product, index) => (
                <ProductFeedCard
                  key={`${product.productKey || product.upc || product.sku}-${index}`}
                  product={product}
                  storeLabel={label}
                  onOpen={() => openDetail(product)}
                  onSave={() => openProduct(product, "save")}
                />
              ))}
            </DealRow>

            {/* Online-only stores pitch in-store clearance from the rail on desktop. */}
            {previewChain ? instoreRow : <div className="lg:hidden">{instoreRow}</div>}

            {topCategories.map((item) => (
              <LazyDealRow
                key={`${retailer.slug}-${item.id}`}
                retailer={retailer}
                categoryId={item.id}
                title={categoryTitle(item.id)}
                meta={`${item.count.toLocaleString()} deals`}
                sort={sort}
                onOpen={(product) => openDetail(product)}
                onSave={(product) => openProduct(product, "save")}
                onViewAll={() => openCategory(item.id)}
                testId={`row-category-${item.id}`}
              />
            ))}
          </div>
          </div>
          </div>
        ) : !query.isLoading && products.length === 0 ? (
          <div className="mx-auto mt-6 max-w-md rounded-[18px] border bg-[var(--hf-surface)] px-5 py-10 text-center" style={{ borderColor: HF.border }}>
            <p className="text-base font-bold">{searchTerm || activeFilters > 0 ? "No deals match that" : `${label} deals are refreshing`}</p>
            <p className="mt-1 text-sm" style={{ color: HF.text3 }}>
              {searchTerm || activeFilters > 0 ? "Try a broader search or fewer filters." : "Check back shortly or pick another store."}
            </p>
            {activeFilters > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="mt-4 h-[38px] rounded-full px-5 text-sm font-bold text-white"
                style={{ background: HF.brand }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : layout === "list" ? (
          <section
            aria-label="Deals"
            aria-busy={query.isLoading}
            className="overflow-hidden rounded-[16px] border bg-[var(--hf-surface)]"
            style={{ borderColor: HF.border }}
            data-testid="feed-list"
          >
            {query.isLoading
              ? Array.from({ length: 10 }, (_, i) => <FeedSkeletonRow key={i} />)
              : products.map((product, index) => {
                  const slot = isLockedSlot(index) ? lockedSlot++ : -1;
                  return (
                    <Fragment key={`${product.productKey || product.upc || product.sku}-${index}`}>
                      {slot >= 0 && renderLockedRow(slot)}
                      <ProductFeedRow
                        product={product}
                        storeLabel={label}
                        onOpen={() => openDetail(product)}
                        onSave={() => openProduct(product, "save")}
                      />
                    </Fragment>
                  );
                })}
          </section>
        ) : (
          <section
            aria-label="Deals"
            aria-busy={query.isLoading}
            className="grid grid-cols-2 gap-2.5 sm:[grid-template-columns:repeat(auto-fill,minmax(210px,1fr))] sm:gap-4 lg:[grid-template-columns:repeat(auto-fill,minmax(180px,1fr))] lg:gap-x-4 lg:gap-y-5"
          >
            {query.isLoading
              ? Array.from({ length: 12 }, (_, i) =>
                  i === 2 && !zip ? (
                    <ZipPromptCard key="zip" introLabel={BASIC_INTRO_LABEL} onSubmit={submitZip} />
                  ) : (
                    <FeedSkeletonCard key={i} />
                  ),
                )
              : products.map((product, index) => {
                  const slot = isLockedSlot(index) ? lockedSlot++ : -1;
                  return (
                    <Fragment key={`${product.productKey || product.upc || product.sku}-${index}`}>
                      {slot >= 0 && renderLockedSlot(slot)}
                      <ProductFeedCard
                        product={product}
                        storeLabel={label}
                        onOpen={() => openDetail(product)}
                        onSave={() => openProduct(product, "save")}
                      />
                    </Fragment>
                  );
                })}
          </section>
        )}

        {/* Runway while more deals are coming: placeholder cards and room below, so a fast scroll
            never hits the page's hard bottom (which makes the sticky header bounce). */}
        {!showSections && hasNextPage && products.length > 0 && (
          layout === "list" ? (
            <div className="mt-2 overflow-hidden rounded-[16px] border" style={{ borderColor: HF.border }} aria-hidden="true">
              {Array.from({ length: 3 }, (_, i) => <FeedSkeletonRow key={i} />)}
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-2.5 sm:[grid-template-columns:repeat(auto-fill,minmax(210px,1fr))] sm:gap-4 lg:[grid-template-columns:repeat(auto-fill,minmax(180px,1fr))] lg:gap-x-4 lg:gap-y-5" aria-hidden="true">
              {Array.from({ length: 10 }, (_, i) => <FeedSkeletonCard key={i} />)}
            </div>
          )
        )}
        {!showSections && (
        <div ref={loadMoreRef} className={`flex items-center justify-center py-5 text-sm ${hasNextPage ? "min-h-[40vh]" : "min-h-16"}`} style={{ color: HF.text3 }}>
          {isFetchingNextPage ? (
            <span role="status">Loading more deals…</span>
          ) : hasNextPage ? (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              className="h-[38px] rounded-full border bg-[var(--hf-surface)] px-5 font-semibold"
              style={{ borderColor: HF.chipBorder, color: HF.text2 }}
            >
              Load more deals
            </button>
          ) : products.length > 0 ? (
            <span>
              That’s every {label} deal right now.{" "}
              <Link to={localSignupUrl("home_feed_end")} onClick={() => trackSignup("feed_end")} className="font-semibold" style={{ color: HF.store }}>
                Unlock in-store clearance
              </Link>{" "}
              · {BASIC_INTRO_LABEL} first month, then {BASIC_PRICE_LABEL}/mo. Cancel anytime.
            </span>
          ) : null}
        </div>
        )}
        </div>
        </div>
      </main>

      {/* On the infinite grid the footer waits for the last page, so it can't bounce away as more loads. */}
      {(showSections || !hasNextPage) && <SiteFooter />}

      {filtersOpen && !showSections && (
        <FeedFilterSheet
          values={filterValues}
          handlers={filterHandlers}
          categories={categories}
          statusCounts={statusCounts}
          total={totalDeals}
          loading={query.isLoading}
          activeCount={activeFilters}
          onClearAll={clearAllFilters}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {selected && (
        <DealDetailModal
          product={selected.product}
          storeLabel={retailerLabel(selected.retailer)}
          storeColor={selected.retailer.accent}
          introLabel={BASIC_INTRO_LABEL}
          priceLabel={BASIC_PRICE_LABEL}
          instoreUrl={localSignupUrl("home_feed_detail_instore")}
          onClose={() => setSelected(null)}
          onGate={(action) => gateFromDetail(selected.product, action)}
          onInstore={() => trackSignup("detail_instore")}
          onOutbound={(url) => {
            let host = "retailer_link";
            try { host = new URL(url).hostname; } catch { /* generic */ }
            track("guest_outbound_clicked", { surface: "web", retailer: retailer.slug, trigger: "deal_detail", source: host });
          }}
        />
      )}

      {pickerOpen && (
        <RetailerPicker
          current={retailer}
          lockedTo={freeMember ? freeUpgradeUrl("home_feed_picker_local") : undefined}
          anchor={pickerAnchor}
          onSelect={(next) => selectRetailer(next)}
          onClose={closePicker}
          onLockedClick={(chainId) =>
            track("guest_signup_started", { surface: "web", retailer: chainId, trigger: "picker_local", source: SOURCE })
          }
        />
      )}

      {shareTarget && (
        <ShareDealSheet
          isOpen
          onClose={() => setShareTarget(null)}
          title={shareTarget.product.name}
          text={`${shareTarget.product.name} for ${sharePriceLine}`}
          priceLine={sharePriceLine}
          imageUrl={shareTarget.product.image ? getProductImageUrl(shareTarget.product.image, "400") : undefined}
          resolveUrl={() => getPublicAffiliateLink(shareTarget.product.productKey || "")}
        />
      )}
      {auth.modal}
    </div>
    </HomeFeedShareContext.Provider>
  );
}

/** A lazily loaded row: one store's newest deals, or one category at a store. */
function LazyDealRow({
  retailer,
  categoryId,
  title,
  meta,
  sort,
  eager = false,
  onOpen,
  onSave,
  onViewAll,
  testId,
}: {
  retailer: PublicDealsRetailer;
  categoryId?: string;
  title: string;
  meta?: string;
  sort: Sort;
  eager?: boolean;
  onOpen: (product: ProductListItem) => void;
  onSave: (product: ProductListItem) => void;
  onViewAll: () => void;
  testId: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(eager || typeof IntersectionObserver === "undefined");

  useEffect(() => {
    const target = ref.current;
    if (visible || !target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisible(true);
    }, { rootMargin: "600px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [visible]);

  const rowQuery = useQuery({
    queryKey: ["public-products-row", retailer.apiRetailer, categoryId || "", sort],
    enabled: visible,
    staleTime: 60_000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    queryFn: () =>
      withRowSlot(() => browsePublicProducts({
        retailer: retailer.apiRetailer,
        storeId: retailer.storeId,
        searchTokens: [],
        categories: categoryId ? [categoryId] : [],
        status: "all",
        ...SORT_REQUEST[sort],
        limit: 12,
      })),
  });
  const products = dedupeByLook(rowQuery.data?.products || []);
  // A store or category with nothing right now simply drops out.
  if (rowQuery.isSuccess && products.length === 0) return null;
  if (rowQuery.isError) {
    return (
      <div ref={ref} className="flex items-center gap-3 px-1 py-1 text-[13.5px]" style={{ color: HF.text3 }} data-testid={`${testId}-error`}>
        <span className="font-bold" style={{ color: HF.ink }}>{title}</span>
        <span>Couldn’t load these deals.</span>
        <button type="button" onClick={() => void rowQuery.refetch()} className="font-bold" style={{ color: HF.store }}>
          Retry
        </button>
      </div>
    );
  }
  const storeLabel = retailerLabel(retailer);

  return (
    <div ref={ref}>
      <DealRow title={title} meta={meta} action={<ViewAllButton onClick={onViewAll} />} testId={testId}>
        {products.length > 0
          ? products.map((product, index) => (
              <ProductFeedCard
                key={`${product.productKey || product.upc || product.sku}-${index}`}
                product={product}
                storeLabel={storeLabel}
                onOpen={() => onOpen(product)}
                onSave={() => onSave(product)}
              />
            ))
          : Array.from({ length: 6 }, (_, i) => <FeedSkeletonCard key={i} />)}
      </DealRow>
    </div>
  );
}

/** Grid / list switch for the "View all" feed; the choice is remembered on this device. */
function LayoutToggle({ mode, onChange }: { mode: LayoutMode; onChange: (mode: LayoutMode) => void }) {
  const options: { value: LayoutMode; label: string; icon: ReactNode }[] = [
    {
      value: "grid",
      label: "Grid",
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></svg>,
    },
    {
      value: "list",
      label: "List",
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" fill="currentColor" /><circle cx="4.5" cy="12" r="1" fill="currentColor" /><circle cx="4.5" cy="18" r="1" fill="currentColor" /></svg>,
    },
  ];
  return (
    <div role="radiogroup" aria-label="Layout" className="flex flex-shrink-0 rounded-[10px] p-[3px]" style={{ background: HF.hairline }}>
      {options.map((option) => {
        const selected = mode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            onClick={() => onChange(option.value)}
            className="flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]"
            style={selected ? { background: HF.surface, color: HF.ink, boxShadow: "0 1px 2px var(--hf-shadow)" } : { color: HF.text3 }}
          >
            {option.icon}
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
